import { ref, type Ref } from 'vue';
import { downloadBlobFile, useCustomToast } from '@/shared/lib';
import { trainingCollaboratorApi } from '@/entities/Training';
import { CERTIFICATE_VIEW_ERROR_MESSAGE } from '../lib/certificateViewMessages';

const PDF_MIME_TYPE = 'application/pdf';

export interface CollaboratorCertificateTarget {
  memberTrainingId: number;
  certificateMimeType: string | null;
  certificateOriginalFileName: string | null;
}

function isPdf(target: CollaboratorCertificateTarget): boolean {
  if (target.certificateMimeType) {
    return target.certificateMimeType === PDF_MIME_TYPE;
  }

  return target.certificateOriginalFileName?.toLowerCase().endsWith('.pdf') ?? false;
}

export function useCollaboratorCertificateViewer(): {
  openingCertificateIds: Ref<number[]>;
  openCertificate: (trainingId: number, target: CollaboratorCertificateTarget) => Promise<void>;
} {
  const { showErrorToast } = useCustomToast();
  const openingCertificateIds = ref<number[]>([]);

  const showFailure = () => {
    showErrorToast(
      { title: CERTIFICATE_VIEW_ERROR_MESSAGE, type: 'error' },
      { timeout: false, closeButton: true }
    );
  };

  const openCertificate = async (trainingId: number, target: CollaboratorCertificateTarget) => {
    const { memberTrainingId } = target;

    if (openingCertificateIds.value.includes(memberTrainingId)) {
      return;
    }

    const shouldOpenInTab = isPdf(target);
    const pendingTab = shouldOpenInTab ? window.open('', '_blank', 'noopener') : null;

    openingCertificateIds.value = [...openingCertificateIds.value, memberTrainingId];

    try {
      const { blob, filename } = await trainingCollaboratorApi.getCollaboratorCertificate(
        trainingId,
        memberTrainingId
      );

      if (shouldOpenInTab) {
        const blobUrl = URL.createObjectURL(blob);

        if (pendingTab) {
          pendingTab.location.href = blobUrl;
        } else {
          window.open(blobUrl, '_blank', 'noopener');
        }

        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        return;
      }

      // The saved name comes from the response header, and not from `certificateOriginalFileName`,
      // because the backend is what builds `certificado_[Nome]_[Matricula].[ext]` — the uploaded
      // name is whatever whoever uploaded it happened to use.
      downloadBlobFile(blob, filename ?? `certificado-${memberTrainingId}`);
    } catch {
      pendingTab?.close();
      showFailure();
    } finally {
      openingCertificateIds.value = openingCertificateIds.value.filter(
        (id) => id !== memberTrainingId
      );
    }
  };

  return { openingCertificateIds, openCertificate };
}
